import { GenerationList } from '@components/generation/generation-row';
import { Avatar, AvatarImage } from '@components/ui/avatar';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@components/ui/pagination';
import { SettingsContainer, SettingsContent } from '@components/ui/settings';
import { Skeleton } from '@components/ui/skeleton';
import useStores from '@hooks/useStore';
import { useWorkflow } from '@hooks/useWorkflow';
import type { AnalyticsModel } from '@models/analytics.model';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyBoxIcon } from '@/components/icons/EmptyBoxIcon';
import { LoaderContainer } from '@/components/ui/loader';
import { cn } from '@/lib/utils';
import { ENV, ENV_TO_READABLE_NAME } from '@/utils/constants';
import { MAX_NAME_LENGTH } from '../generation';
import AnalyticsMonitor from './AnalyticsMonitor';
import { podStatusToProgress } from './analyticsDetails/AnalyticsCard';
import { getDuration, getElapsedTime } from './utils';

const Analytics = () => {
  const { workflow } = useWorkflow();

  const { analyticsStore, workflowStore } = useStores();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const computeCurrentPage = () => {
    const currentPageParam = searchParams.get('page');
    if (!currentPageParam) {
      return 1;
    }
    const page = Number.parseInt(currentPageParam, 10);
    if (
      page < 1 ||
      page > (workflow?.analyticPageStore.getTotalNumberOfPages() ?? 0)
    ) {
      return 1;
    }
    return page;
  };

  const currentPage = computeCurrentPage();

  let analytics: AnalyticsModel[] = [];
  let jobExecutions: AnalyticsModel['jobExecutions'] = [];

  useEffect(() => {
    if (!workflow) {
      return;
    }
    analyticsStore.startPolling(workflow);
    return () => analyticsStore.stopAllPolling();
  }, [workflow, analyticsStore]);

  if (!workflow) {
    return (
      <LoaderContainer>
        <Skeleton className="h-full w-full" />
      </LoaderContainer>
    );
  }

  if (!workflow.analyticPageStore.isPageCached(currentPage)) {
    void workflowStore.fetchAnalyticsForWorkflow(workflow, currentPage);
  }

  analytics = workflow.analyticPageStore.getPageItems(currentPage);
  if (analytics?.[0]) {
    jobExecutions = analyticsStore.getJobExecutionPageItems(
      analytics[0].id,
      currentPage
    );
  }
  const numberOfPages = workflow.analyticPageStore.getTotalNumberOfPages() ?? 0;
  return (
    <SettingsContainer>
      <SettingsContent>
        <div>
          {analyticsStore.isAnalyticsPageFetching(workflow.id, currentPage) && (
            <Skeleton className="h-[100px] w-full" />
          )}
          {/* TODO : Add create Analytics */}

          {!analyticsStore.isAnalyticsPageFetching(workflow.id, currentPage) &&
          analytics.length < 1 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <EmptyBoxIcon className="size-16" />
              <p className="text-center text-muted-foreground text-sm">
                There is no active Analytics ETL on this workflow
              </p>
            </div>
          ) : (
            <>
              {analytics?.map(analytic => (
                <div key={analytic.id}>
                  <AnalyticsMonitor analytics={analytic} />
                </div>
              ))}

              <div className="mt-11">
                <GenerationList className="text-zinc-500">
                  {jobExecutions?.map((jobExec, index) => {
                    const isFirst = index === 0;
                    const isLast = index === jobExecutions.length - 1;
                    return (
                      <a
                        className={cn(
                          'block border border-black/[0.08] border-b-0 bg-white p-4 text-sm hover:cursor-pointer hover:bg-gray-50',
                          isFirst && 'rounded-t-md',
                          isLast && 'rounded-b-md border-b'
                        )}
                        key={jobExec.id}
                        onClick={() =>
                          navigate(`${analytics[0].id}/${jobExec.id}`)
                        }
                      >
                        <div className="grid w-full grid-cols-[1fr_9rem_1fr] items-center gap-4">
                          <div className="flex-1">
                            <div className="font-medium text-zinc-950">
                              {jobExec.id}
                            </div>
                            <div className="flex flex-row items-center">
                              {ENV_TO_READABLE_NAME[ENV]}
                            </div>
                          </div>
                          <div className="shrink-0 grow-0 basis-36">
                            <div className="flex flex-row items-center">
                              {podStatusToProgress(jobExec.status)}
                              <div className="ml-2.5 font-medium">
                                {jobExec.status}
                              </div>
                            </div>
                            <div className="pl-6">
                              {getDuration(
                                jobExec.executionTime ?? undefined,
                                jobExec.lastUpdatedTime ?? undefined
                              )}{' '}
                              {getElapsedTime(
                                jobExec.executionTime ?? undefined
                              )}
                            </div>
                          </div>
                          <div className="justify-self-end">
                            <div className="flex flex-row items-center">
                              {getElapsedTime(
                                jobExec.executionTime ?? undefined
                              )}{' '}
                              by{' '}
                              {analytics[0].createdBy?.slice(
                                0,
                                MAX_NAME_LENGTH
                              )}
                              <Avatar className="ml-2 h-6 w-6">
                                <AvatarImage
                                  src={
                                    'https://www.w3schools.com/howto/img_avatar.png'
                                  }
                                />
                              </Avatar>
                            </div>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </GenerationList>
              </div>
            </>
          )}
        </div>

        {workflow.analyticPageStore.getTotalNumberOfItemsInDB() > 0 && (
          <Pagination className="mt-3">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => {
                    navigate(
                      currentPage <= 1
                        ? `?page=${currentPage}`
                        : `?page=${currentPage - 1}`
                    );
                  }}
                />
              </PaginationItem>

              {Array.from(
                { length: numberOfPages },
                (_, index) => index + 1
              ).map(pageNumber => {
                const shouldShowPage =
                  pageNumber === 1 ||
                  pageNumber === numberOfPages ||
                  Math.abs(pageNumber - currentPage) <= 1;

                if (!shouldShowPage) {
                  if (pageNumber === 2 || pageNumber === numberOfPages - 1) {
                    return (
                      <PaginationItem key={`ellipsis-${pageNumber}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return null;
                }

                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      isActive={pageNumber === currentPage}
                      onClick={() =>
                        setSearchParams({ page: pageNumber.toString() })
                      }
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    navigate(
                      currentPage >= numberOfPages
                        ? `?page=${currentPage}`
                        : `?page=${currentPage + 1}`
                    )
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </SettingsContent>
    </SettingsContainer>
  );
};

export default observer(Analytics);
